import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, Download, Search, X, Calendar, Car } from 'lucide-react';
import { CarpoolVehicle } from '../types';
import baserowApi from '../services/baserowApi';

// Typen für Baserow-Dokumente
interface BaserowDocument {
  id: string | number; // Changed to allow string IDs
  title?: string;
  file_name?: string;
  file_url?: string;
  file_size?: number;
  vehicle_id?: number | number[];
  type?: string | { id: number; value: string; color: string };
  created_on?: string;
  display?: string;
  url?: string;
  size?: number;
  created_at?: string;
  carpool?: { id: number }[];
  carpool_license_check?: { id: number }[];
  original_doc_name?: string; // Added for the new API structure
  files?: { 
    url: string;
    size: number;
    name: string;
    uploaded_at: string;
  }[];
}

interface DocumentStorageProps {
  vehicles: CarpoolVehicle[];
  archivedVehicles: CarpoolVehicle[];
  onRestore?: (id: number) => void;
  vehicleDocuments?: Record<string, Record<string, string[]>>;
  archivedVehicleDocuments?: Record<string, Record<string, string[]>>;
  tab?: 'fahrzeuge' | 'archiv';
  setTab?: (tab: 'fahrzeuge' | 'archiv') => void;
}

const DocumentStorage: React.FC<DocumentStorageProps> = ({ 
  vehicles, 
  archivedVehicles, 
  onRestore = () => {}, 
  vehicleDocuments = {},
  archivedVehicleDocuments = {},
  tab = 'fahrzeuge',
  setTab = () => {}
}) => {
  const [documents, setDocuments] = useState<BaserowDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<number | null>(null);
  // Sortierung
  const [sortBy, setSortBy] = useState<'name' | 'license' | 'date' | 'size'>('license');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Handler für Klicksortierung – vermeidet doppelte setState-Auswertungen
  const handleHeaderSort = (key: 'name' | 'license' | 'date' | 'size') => {
    if (sortBy === key) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadVehicle, setUploadVehicle] = useState<number | null>(null);
  const [uploadType, setUploadType] = useState<string>('other');

  // Debug-Helfer wurden entfernt, um Ladezeiten zu reduzieren

  // Dokumente aus Baserow laden
  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await baserowApi.getDocuments();
      if (response.results && response.results.length > 0) {
        setDocuments(response.results);
      } else {
        setDocuments([]);
      }
    } catch (error: any) {
      console.error('Fehler beim Laden der Dokumente:', error);
      alert(`Fehler beim Laden der Dokumente: ${error.message}`);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Dokumente beim Laden der Komponente und beim Tab-Wechsel laden
  useEffect(() => {
    loadDocuments();
  }, [tab]);

  // Gefilterte Dokumente
  const filteredDocuments = documents.filter(doc => {

    // Suchterm-Filter
    const matchesSearch = !searchTerm || 
      (doc.title && doc.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (doc.file_name && doc.file_name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Fahrzeug-Filter
    const matchesVehicle = !selectedVehicle || 
      (doc.carpool && Array.isArray(doc.carpool) && doc.carpool.some(v => v.id === selectedVehicle)) ||
      (doc.vehicle_id && (Array.isArray(doc.vehicle_id) ? doc.vehicle_id.includes(selectedVehicle) : doc.vehicle_id === selectedVehicle));

    return matchesSearch && matchesVehicle;
  });

  // Sortierte Dokumente nach Listenstruktur
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    // Hilfswerte
    const aName = (a.title || a.file_name || a.original_doc_name || a.display || '').toString().toLowerCase();
    const bName = (b.title || b.file_name || b.original_doc_name || b.display || '').toString().toLowerCase();
    const aVehicleId: any = a.carpool?.[0]?.id || (Array.isArray(a.vehicle_id) ? a.vehicle_id[0] : a.vehicle_id);
    const bVehicleId: any = b.carpool?.[0]?.id || (Array.isArray(b.vehicle_id) ? b.vehicle_id[0] : b.vehicle_id);
    const aLicense = getVehicleName(aVehicleId).toLowerCase();
    const bLicense = getVehicleName(bVehicleId).toLowerCase();
    const aDate = new Date(a.created_at || a.created_on || 0).getTime();
    const bDate = new Date(b.created_at || b.created_on || 0).getTime();
    const aSize = (a.files && a.files[0] ? a.files[0].size : a.file_size) || 0;
    const bSize = (b.files && b.files[0] ? b.files[0].size : b.file_size) || 0;

    let cmp = 0;
    switch (sortBy) {
      case 'name':
        cmp = aName.localeCompare(bName);
        break;
      case 'license':
        cmp = aLicense.localeCompare(bLicense);
        break;
      case 'date':
        cmp = aDate - bDate;
        break;
      case 'size':
        cmp = aSize - bSize;
        break;
      default:
        cmp = 0;
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  // Datei hochladen
  const handleFileUpload = async () => {
    if (!selectedFile || !uploadVehicle) return;

    try {
      setUploading(true);
      
      // 1. Datei hochladen
      const uploadResponse = await baserowApi.uploadFile(selectedFile);
      
      // 2. Dokument in der Dokumenten-Tabelle anlegen - wie Python-Code
      const documentData = {
        title: selectedFile.name,
        description: `Hochgeladen über DocumentStorage`,
        files: [uploadResponse.name], // Nur der Dateiname
        active: true,
        content_type: 'application/pdf',
        type: uploadType,
        carpool: [uploadVehicle]
      };
      
      await baserowApi.createDocument(documentData);
      
      // 3. Dokument erfolgreich erstellt - lade Dokumente neu
      await loadDocuments();
      
      setUploading(false);
      setSelectedFile(null);
      setShowUploadDialog(false);
      setUploadVehicle(null);
      setUploadType('other');
      alert(`Datei "${selectedFile.name}" erfolgreich hochgeladen!`);
    } catch (error) {
      console.error('Fehler beim Hochladen:', error);
      setUploading(false);
      // Bessere Fehlerbehandlung
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      alert(`Fehler beim Hochladen: ${errorMessage}`);
    }
  };

  // Datei öffnen
  const handleFileOpen = async (fileName: string, fileUrl?: string) => {
    try {
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      } else {
        alert('Keine URL für diese Datei verfügbar.');
      }
    } catch (error) {
      console.error('Fehler beim Öffnen der Datei:', error);
      alert('Fehler beim Öffnen der Datei: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  // Datei herunterladen
  const handleFileDownload = async (fileName: string, fileUrl?: string) => {
    try {
      if (fileUrl) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Keine URL für diese Datei verfügbar.');
      }
    } catch (error) {
      console.error('Fehler beim Herunterladen der Datei:', error);
      alert('Fehler beim Herunterladen der Datei: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  // Fahrzeug-Name sicher extrahieren (als Funktionsdeklaration, damit hoisting funktioniert)
  function getVehicleName(vehicleId: number | string | undefined): string {
    if (!vehicleId) return 'Kein Fahrzeug zugeordnet';
    const id = typeof vehicleId === 'string' ? parseInt(vehicleId, 10) : vehicleId;
    if (isNaN(id as number)) return 'Ungültige Fahrzeug-ID';
    const vehicle = getActiveVehicleList().find(v => v.id === id);
    if (vehicle) {
      return vehicle.license_plate || vehicle.model || `Fahrzeug ${vehicle.id}`;
    }
    return `Fahrzeug ${id}`;
  }

  // Typ-Namen sicher extrahieren
  const getTypeName = (type: any): string => {
    if (!type) return 'Unbekannt';
    
    if (typeof type === 'object' && type !== null) {
      if (type.value && typeof type.value === 'string') {
        return type.value;
      }
      if (type.id && typeof type.id === 'string') {
        return type.id;
      }
      // Fallback: versuche das Objekt zu stringifizieren
      try {
        return JSON.stringify(type);
      } catch {
        return 'Unbekannt';
      }
    }
    
    if (typeof type === 'string') {
      return type;
    }
    
    if (typeof type === 'number') {
      return type.toString();
    }
    
    return 'Unbekannt';
  };

  // Dateigröße formatieren
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unbekannt';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Datum formatieren
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unbekannt';
    try {
      return new Date(dateString).toLocaleDateString('de-DE');
    } catch {
      return 'Unbekannt';
    }
  };

  // Sichere String-Konvertierung
  const safeString = (value: any): string => {
    if (value === null || value === undefined) return 'Unbekannt';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return 'Unbekannt';
      }
    }
    return String(value);
  };

  // Filter zurücksetzen
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedVehicle(null);
    // keine Typ-Filterung mehr
  };

  // Hoisting-sichere Deklaration
  function getActiveVehicleList() {
    return tab === 'fahrzeuge' ? vehicles : archivedVehicles;
  }

  return (
    <div className="p-8 bg-white min-h-screen">
      <div className="mb-6">
        <div className="grid grid-cols-3 items-center">
          <div className="justify-self-start">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              <span>Zurück zum Hub</span>
            </Link>
          </div>
          <div className="justify-self-center">
            <h2 className="text-3xl font-bold text-gray-900 ">Dokumentenablage</h2>
          </div>
          <div className="justify-self-end"></div>
        </div>
      </div>
      
      {/* Upload-Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowUploadDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#add015] text-black rounded-lg hover:bg-[#add015] hover:bg-opacity-80 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Neues Dokument hochladen
        </button>
      </div>

      {/* Tabs für Fahrzeuge und Archiv */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 ">
        <button
          className={`px-6 py-3 -mb-px font-semibold border-b-2 transition-colors ${tab === 'fahrzeuge' ? 'border-[#add015] text-[#add015] bg-white' : 'border-transparent text-gray-500 bg-gray-100'}`}
          onClick={() => { setTab('fahrzeuge'); clearFilters(); }}
        >
          Fahrzeuge
        </button>
        <button
          className={`px-6 py-3 -mb-px font-semibold border-b-2 transition-colors flex items-center gap-1 ${tab === 'archiv' ? 'border-[#add015] text-[#add015] bg-white' : 'border-transparent text-gray-500 bg-gray-100'}`}
          onClick={() => { setTab('archiv'); clearFilters(); }}
        >
          Archiv
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
      </div>

      {/* Filterbar */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Suchfeld */}
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Dokumente durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                 className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#add015] focus:border-transparent"
              />
            </div>
          </div>

          {/* Fahrzeug-Filter */}
          <div className="w-48">
            <select
              value={selectedVehicle || ''}
              onChange={(e) => setSelectedVehicle(e.target.value ? Number(e.target.value) : null)}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#add015] focus:border-transparent"
            >
              <option value="">Alle Fahrzeuge</option>
              {getActiveVehicleList().map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.license_plate || vehicle.model || `Fahrzeug ${vehicle.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* Sortierung in der Filterbar */}
          <div className="flex items-center gap-2 ml-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-48 h-10 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
            >
              <option value="license">Kennzeichen</option>
              <option value="name">Name</option>
              <option value="date">Hochgeladen</option>
              <option value="size">Größe</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              className="w-48 h-10 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
            >
              <option value="asc">Aufsteigend</option>
              <option value="desc">Absteigend</option>
            </select>
          </div>

          {/* Filter zurücksetzen */}
          {(searchTerm || selectedVehicle) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="w-4 h-4" />
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Dokumente-Tabelle */}
      <div className="bg-white  rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 ">Lade Dokumente...</p>
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 ">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider cursor-pointer select-none"
                      onClick={() => handleHeaderSort('name')}>
                    <span className="inline-flex items-center gap-1">Dokument {sortBy === 'name' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider cursor-pointer select-none"
                      onClick={() => handleHeaderSort('license')}>
                    <span className="inline-flex items-center gap-1">Fahrzeug {sortBy === 'license' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider cursor-pointer select-none"
                      onClick={() => handleHeaderSort('size')}>
                    <span className="inline-flex items-center gap-1">Größe {sortBy === 'size' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider cursor-pointer select-none"
                      onClick={() => handleHeaderSort('date')}>
                    <span className="inline-flex items-center gap-1">Datum {sortBy === 'date' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white  divide-y divide-gray-200 ">
                {sortedDocuments.map((doc) => {
                  const vehicleId = doc.carpool?.[0]?.id || (Array.isArray(doc.vehicle_id) ? doc.vehicle_id[0] : doc.vehicle_id);
                  const fileName = safeString(doc.title || doc.file_name || doc.original_doc_name || doc.display || 'Unbekanntes Dokument');
                  const fileUrl = doc.file_url || (doc.files && doc.files[0] ? doc.files[0].url : '');
                  const fileSize = doc.files && doc.files[0] ? doc.files[0].size : doc.file_size;
                  const uploadDate = doc.created_at || doc.created_on;
                  
                  return (
                    <tr key={String(doc.id)} className="hover:bg-gray-50  transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FileText className="w-5 h-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 ">
                              {fileName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Car className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 ">
                            {getVehicleName(vehicleId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                          {getTypeName(doc.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 ">
                        {formatFileSize(fileSize)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900 ">
                            {formatDate(uploadDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleFileOpen(fileName, fileUrl)}
                            className="text-[#add015] hover:text-[#add015] hover:opacity-80"
                            title="Öffnen"
                          >
                            Öffnen
                          </button>
                          <button
                            onClick={() => handleFileDownload(fileName, fileUrl)}
                            className="text-[#add015] hover:text-[#add015] hover:opacity-80"
                            title="Herunterladen"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 ">
              {searchTerm || selectedVehicle 
                ? 'Keine Dokumente gefunden, die den Filterkriterien entsprechen.'
                : 'Keine Dokumente vorhanden.'
              }
            </p>
            <p className="text-sm mt-2 text-gray-400 ">
              Laden Sie Ihre ersten Dokumente hoch.
            </p>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      {showUploadDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 ">Neues Dokument hochladen</h3>
            
            {/* Fahrzeug-Auswahl */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700  mb-2">
                Fahrzeug
              </label>
              <select
                value={uploadVehicle || ''}
                onChange={(e) => setUploadVehicle(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300  rounded-lg bg-white  text-gray-900  focus:ring-2 focus:ring-[#add015] focus:border-transparent"
                required
              >
                <option value="">Fahrzeug auswählen</option>
                {getActiveVehicleList().map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.license_plate || vehicle.model || `Fahrzeug ${vehicle.id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Typ-Auswahl */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700  mb-2">
                Dokument-Typ
              </label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300  rounded-lg bg-white  text-gray-900  focus:ring-2 focus:ring-[#add015] focus:border-transparent"
              >
                <option value="offer">Angebote</option>
                <option value="contract">Verträge</option>
                <option value="police">Fahrzeugschein</option>
                <option value="certificate">TÜV</option>
                <option value="order-confirmation">Bestellungen</option>
                <option value="invoice">Rechnungen</option>
                <option value="other">Sonstiges</option>
              </select>
            </div>

            {/* Datei-Auswahl */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700  mb-2">
                Datei
              </label>
              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="w-full p-2 border border-gray-300  rounded-lg bg-white  text-gray-900 "
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleFileUpload}
                disabled={!selectedFile || !uploadVehicle || uploading}
                className="flex-1 px-4 py-2 bg-[#add015] text-black rounded hover:bg-[#add015] hover:bg-opacity-80 disabled:opacity-50"
              >
                {uploading ? 'Hochladen...' : 'Hochladen'}
              </button>
              <button
                onClick={() => {
                  setShowUploadDialog(false);
                  setSelectedFile(null);
                  setUploadVehicle(null);
                  setUploadType('other');
                }}
                className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700  rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentStorage; 